import type { DocumentSection } from "@/api/schemas";

/**
 * F3 — placeholder visibility (pokaži-ili-priznaj on the delivery screen).
 *
 * Generated documents can carry two "not done yet" signals the architect
 * MUST see before submitting: the ``[ ZA RUČNI UNOS ]`` marker (a field the
 * generator couldn't fill) and the ``[… NIJE U BAZI …]`` plan placeholder
 * (no spatial plan could be cited). A whole section can also be a
 * ``source: "placeholder"`` stub the architect writes personally.
 *
 * This module detects those signals on a section and projects them onto
 * the assembled Mapa 1 Sadržaj (whose backend entries carry only titles and
 * page numbers — the flags are re-derived here by title match, because the
 * API contract stays untouched).
 */

export interface PlaceholderInfo {
  /** Occurrences of the manual-entry marker (≥1 for placeholder stubs). */
  rucniUnos: number;
  /** Section cites the plan-missing placeholder instead of a real plan. */
  planMissing: boolean;
}

const RUCNI_UNOS_RE = /\[\s*ZA\s+RUČNI\s+UNOS\s*\]/g;
const PLAN_MISSING_RE = /\[[^\]]*NIJE\s+U\s+BAZI[^\]]*\]/;

/** Signals on one section, or null when there's nothing to flag. */
export function placeholderInfo(
  section: Pick<DocumentSection, "content" | "source">,
): PlaceholderInfo | null {
  const content = section.content ?? "";
  const rucniUnos = content.match(RUCNI_UNOS_RE)?.length ?? 0;
  const planMissing = PLAN_MISSING_RE.test(content);
  // A placeholder stub is by definition awaiting the architect, marker or not.
  const stub = section.source === "placeholder";
  if (!rucniUnos && !planMissing && !stub) return null;
  return { rucniUnos: rucniUnos || (stub ? 1 : 0), planMissing };
}

/** Short badge label — the loudest signal wins the (limited) space. */
export function placeholderLabel(info: PlaceholderInfo): string {
  return info.planMissing ? "plan nije u bazi" : "za ručni unos";
}

/** Tooltip/summary sentence: what exactly is unfinished and what to do. */
export function placeholderTooltip(info: PlaceholderInfo): string {
  const parts: string[] = [];
  if (info.planMissing) {
    parts.push(
      "Umjesto citata prostornog plana stoji oznaka [NIJE U BAZI] — učitaj plan i regeneriraj.",
    );
  }
  if (info.rucniUnos > 0) {
    parts.push(
      info.rucniUnos === 1
        ? "Sadrži oznaku [ ZA RUČNI UNOS ] — dopuni prije predaje."
        : `Sadrži ${info.rucniUnos} oznake [ ZA RUČNI UNOS ] — dopuni prije predaje.`,
    );
  }
  return parts.join("\n");
}

/** Sadržaj titles come back verbatim from the assembler; normalise both
 * sides so an extra space or case difference can't hide a flag. */
function normTitle(title: string): string {
  return title.trim().replace(/\s+/g, " ").toLowerCase();
}

/** title → flags map over every section that goes into the bound PDF. */
export function placeholderFlagsByTitle(
  sections: Array<Pick<DocumentSection, "title" | "content" | "source">>,
): Map<string, PlaceholderInfo> {
  const out = new Map<string, PlaceholderInfo>();
  for (const s of sections) {
    const info = placeholderInfo(s);
    if (!info) continue;
    const key = normTitle(s.title ?? "");
    const prior = out.get(key);
    out.set(
      key,
      prior
        ? {
            rucniUnos: prior.rucniUnos + info.rucniUnos,
            planMissing: prior.planMissing || info.planMissing,
          }
        : info,
    );
  }
  return out;
}

/** Flags for one Sadržaj entry (or sub-entry), by title. */
export function placeholderForTitle(
  map: Map<string, PlaceholderInfo>,
  title: string | null | undefined,
): PlaceholderInfo | null {
  if (!title) return null;
  return map.get(normTitle(title)) ?? null;
}
